/* Tenant workspace persistence — save/restore UI state per tenant.
   V1 scope: open editor tabs (with active tab + structure visibility) and chat history.
   Diff/quote-frame/console state and dirty buffers are deferred to future iterations. */

import { api } from './api.js';
import { state } from './state.js';
import { wsSend } from './ws.js';
import { confirm } from './dialogs.js';
import {
  getSerializableMessages,
  restoreMessages,
  clearChat,
} from './chat.js';
import {
  openFileTab,
  closeFileTab,
  switchWorkspaceTab,
} from './search.js';
import { notify } from './toast.js';

const WORKSPACE_VERSION = 1;

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------

function _serializeTab(tab) {
  return {
    path: tab.path,
    label: tab.label,
    language: tab.language || null,
    kind: tab.kind || null,
    active: tab.id === state.activeWorkspaceTab,
    structureVisible: !!(tab._structurePanel && tab._structurePanel.element &&
                         tab._structurePanel.element.hidden === false),
  };
}

function _buildSnapshot() {
  const openTabs = (state.workspaceTabs || []).map(_serializeTab);
  return {
    version: WORKSPACE_VERSION,
    savedAt: new Date().toISOString(),
    openTabs,
    chat: { messages: getSerializableMessages() },
  };
}

// ---------------------------------------------------------------------------
// Lost-items inventory (for the save-failure dialog)
// ---------------------------------------------------------------------------

export function collectLostItems() {
  const tabs = state.workspaceTabs || [];
  return {
    tabCount: tabs.length,
    chatCount: getSerializableMessages().length,
  };
}

/**
 * Prompt the user before abandoning unsaved workspace state.
 * Note: dialogs.js confirm(title, message) accepts plain-text strings only.
 * The spec's object-form signature is kept here; we flatten to two args internally.
 */
export async function confirmLossDialog(fromName, inventory, error) {
  const lines = [];
  if (inventory.tabCount) lines.push(`${inventory.tabCount} open tab(s)`);
  if (inventory.chatCount) lines.push(`${inventory.chatCount} chat message(s)`);

  const errMsg = (error && error.message) || String(error || 'unknown');
  const lossList = lines.length
    ? `If you continue, you will lose: ${lines.join(', ')}.`
    : 'Nothing significant will be lost.';

  const message =
    `Failed to save workspace for tenant "${fromName}":\n${errMsg}\n\n${lossList}\n\nSwitch anyway and lose this state?`;

  return await confirm('Switch tenant anyway?', message);
}

// ---------------------------------------------------------------------------
// Public lifecycle
// ---------------------------------------------------------------------------

export async function saveCurrentWorkspace(name) {
  if (!name) return;
  const snapshot = _buildSnapshot();
  await api.workspacePut(name, snapshot);
}

export function tearDownTenantScopedUI() {
  // Close all editor tabs
  const tabs = (state.workspaceTabs || []).slice();  // copy — we mutate while iterating
  for (const t of tabs) closeFileTab(t.id);
  // Empty chat
  clearChat();
}

export async function restoreWorkspace(workspace) {
  if (!workspace) return;
  if (workspace._malformed) {
    notify('Workspace file was malformed; starting fresh.', 'warning');
    return;
  }
  if (workspace.version !== WORKSPACE_VERSION) {
    if (workspace.version != null) {
      notify(`Workspace version ${workspace.version} not supported; starting fresh.`, 'warning');
    }
    return;
  }
  state.tenantWorkspaceLoading = true;
  try {
    const tabs = Array.isArray(workspace.openTabs) ? workspace.openTabs : [];
    let activeTabId = null;

    for (const saved of tabs) {
      if (!saved || !saved.path) continue;
      // openFileTab is sync-ish; the content load happens in the background.
      openFileTab(
        saved.path,
        saved.label || saved.path.split('/').pop(),
        saved.language || null,
        null,  // no highlightLine on restore
      );
      // The just-pushed tab is now in state.workspaceTabs.
      const newTab = state.workspaceTabs[state.workspaceTabs.length - 1];
      if (newTab) {
        // Mirror the save side (line 33): structureVisible=true → panel shown, =false → hidden.
        newTab._structurePanelHidden = saved.structureVisible === false;
        if (saved.active) activeTabId = newTab.id;
      }
    }

    if (activeTabId) switchWorkspaceTab(activeTabId);

    // Chat messages
    const msgs = (workspace.chat && Array.isArray(workspace.chat.messages))
      ? workspace.chat.messages : [];
    if (msgs.length) {
      restoreMessages(msgs);
      // Hydrate server-side LLM context
      try {
        wsSend({
          type: 'chat/restore',
          messages: msgs.map(m => ({ role: m.role, content: m.content })),
        });
      } catch {
        // wsSend swallows non-open sockets in most setups; if not, ignore.
      }
    }
  } finally {
    state.tenantWorkspaceLoading = false;
  }
}
